<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, provide, ref, watch } from "vue";
import { useRouter } from "vue-router";
import {
  FormResponse,
  FormResponseStatus,
  FormType,
  HousingApplication,
  HousingSiteResult,
  Workflow,
} from "~/api";
import {
  useWorkflow,
  workflowInjectKey,
} from "~/Compliance/composables/useWorkflow";
import FormResponseTemplate from "~/Compliance/pages/FormResponseTemplate.vue";
import { FormDisplayMode } from "~/NewFormBuilder/types/FormDisplayMode";
import { applicationRouteMap } from "~/router/mainRouter";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { useHousingOrgStore } from "~/YYC_Housing/stores/HousingOrgStore";
import { useHousingSiteStore } from "~/YYC_Housing/stores/HousingSiteStore";
import ApplicationWizardSteps from "../components/ApplicationWizardSteps.vue";
import ReviewAndSubmitStep from "../components/ReviewAndSubmitStep.vue";
import FinderApplication from "../pages/FinderApplication.vue";
import { ApplicationWizardStep } from "../types/plusTypes";

const props = defineProps<{
  applicationGuid: string;
  step: number;
}>();

const router = useRouter();
const housingOrgStore = useHousingOrgStore();
const housingSiteStore = useHousingSiteStore();
const myInfoStore = useMyInfoStore();

const smallScreen = ref<boolean>(window.innerWidth <= 1040);

const updateScreenWidth = () => {
  smallScreen.value = window.innerWidth <= 1040;
};

/** The current step the user is on in their application */
// const currentApplicationStep = ref(props.step);
/** The current application we are looking at (the application associated with the given guid)*/
const currentApplication = ref<HousingApplication | undefined>(undefined);
const updateCount = ref(0);

// Workflow state for inline rendering
const workflow = ref<Workflow>();
const sharedAnswers = ref<Record<string, string>>({});

watch(currentApplication, () => {
  updateCount.value++;
});

// TODO: Main Application form definition GUID — 420bda07-06c4-4772-8d45-28a277cec11d
const applicationFormResponse = computed<FormResponse | undefined>(() =>
  currentApplication.value?.formResponses?.find(
    (form) =>
      form.formDefinitionGuid === "420bda07-06c4-4772-8d45-28a277cec11d",
  ),
);

const workflowStepForms = computed<FormResponse[]>(
  () =>
    workflow.value?.formResponses
      ?.filter((f) => f.formType === FormType.WORKFLOW_STEP)
      .sort(
        (a, b) =>
          (a.formDefinition?.sortOrder ?? 0) -
          (b.formDefinition?.sortOrder ?? 0),
      ) ?? [],
);

const updateSiteResultsInApplication = (sites: HousingSiteResult[]) => {
  if (currentApplication.value) {
    currentApplication.value.siteResults = sites;
  }
};

const getHousingApplication = async () => {
  currentApplication.value =
    await apiClient.housingApplication.getHousingApplication(
      props.applicationGuid,
    );
};

const loadWorkflow = async () => {
  const entityGuid = currentApplication.value?.entityGuid;
  if (!entityGuid) return;

  const linkedWorkflows =
    await apiClient.workflowInstance.getLinkedWorkflowsWithArchived(
      entityGuid,
      false,
    );

  const housingAppWorkflow = linkedWorkflows.find(
    (w) => w.workflowDefinitionName === "Housing Application",
  );
  if (!housingAppWorkflow) return;

  const content = await apiClient.workflowInstance.getWorkflow(
    housingAppWorkflow.workflowGuid,
  );

  sharedAnswers.value = content.sharedAnswers;
  workflow.value = content.workflow;

  // Copy formDefinitions to formResponses (same pattern as WorkflowResponseViewer.extend())
  for (const fr of workflow.value?.formResponses ?? []) {
    const matchingDef =
      workflow.value?.workflowDefinition?.formDefinitions?.find(
        (fd) => fd.guid === fr.formDefinitionGuid,
      );
    if (matchingDef) {
      fr.formDefinition = matchingDef;
      fr.formType = matchingDef.formType;
    }
  }
};

watch(
  () => applicationFormResponse.value?.status,
  async (status) => {
    if (status === FormResponseStatus.READY_FOR_SUBMISSION) {
      router.push({
        name: applicationRouteMap.Intake.children.Application.children
          .ApplicationWizard.name,
        params: {
          step: ApplicationWizardStep.ReviewAndSubmit,
          applicationGuid: props.applicationGuid,
        },
      });
    }
  },
);

const workflowState = useWorkflow({
  updateStatus: async (_status: string) => {
    showError("Updating workflow status is not supported in this context");
  },
  updateStatusFromNameStamp: async (status: string) => {
    if (!workflow.value?.guid) {
      showError("Workflow instance not found");
      return;
    }
    if (workflow.value.status === status) {
      return;
    }
    const previousStatus = workflow.value.status;
    workflow.value.status = status;
    try {
      await apiClient.workflowInstance.updateWorkflow(
        workflow.value.guid,
        workflow.value,
      );
    } catch (error) {
      workflow.value.status = previousStatus;
      showError("Failed to update workflow status", error);
    }
  },
  getWorkflowStatus: () => workflow.value?.status ?? "",
  getWorkflow: () => workflow.value,
  deleteWorkflow: async () => {
    showError("Deleting workflow is not supported in this context");
  },
});

// provide form state to child components
provide(workflowInjectKey, workflowState);

const loading = ref(true);
onMounted(async () => {
  await myInfoStore.load();
  const proms = [housingOrgStore.load(), housingSiteStore.load()];
  if (myInfoStore.isLoggedIn) {
    proms.push(getHousingApplication());
  }
  await Promise.all(proms);

  // Load the workflow after the application is available
  await loadWorkflow();
  loading.value = false;

  window.addEventListener("resize", updateScreenWidth);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateScreenWidth);
});
</script>

<template>
  <div v-loading="loading" class="applicationWizardPage">
    <div class="applicationWizardPageInner">
      <ApplicationWizardSteps
        v-if="currentApplication"
        :key="updateCount"
        class="steps"
        :step="step"
        :application="currentApplication"
        :workflow="workflow"
        :show-current-step-only="smallScreen"
        :icon-background="`var(--yych-background-color)`"
        show-next
        show-prev
        simple
      />
      <div class="wizard-content">
        <template v-if="currentApplication">
          <div
            v-if="step === ApplicationWizardStep.SelectLocations"
            class="applicationStep"
          >
            <FinderApplication
              v-if="step === 0"
              :key="updateCount"
              class="finder"
              :application="currentApplication"
              @update-selected="
                ($event: HousingSiteResult[]) =>
                  updateSiteResultsInApplication($event)
              "
            />
          </div>

          <div
            v-if="step === ApplicationWizardStep.ApplicationForm"
            class="applicationStep"
          >
            <div v-if="workflow" class="workflowFormsContainer">
              <FormResponseTemplate
                v-for="formStep in workflowStepForms"
                :key="formStep.guid"
                class="form"
                :form-guid="formStep.formDefinitionGuid"
                :response-guid="formStep.guid"
                :response="formStep"
                :workflow="workflow"
                :incoming-display-mode="
                  formStep.status === FormResponseStatus.SUBMITTED
                    ? FormDisplayMode.READ_ONLY
                    : FormDisplayMode.EDIT
                "
                :shared-answers="sharedAnswers"
                :on-workflow-refresh="loadWorkflow"
              />
            </div>
            <div v-else-if="currentApplication?.entityGuid" v-loading="true" />
            <p v-else>NO WORKFLOW FOUND</p>
          </div>

          <div
            v-if="step >= ApplicationWizardStep.ReviewAndSubmit"
            class="applicationStep"
          >
            <div class="reviewAndSubmitStep">
              <ReviewAndSubmitStep
                :key="updateCount"
                :application="currentApplication"
                :workflow="workflow"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="less">
.el-main:has(.applicationWizardPage) {
  padding: 0;
}
.applicationWizardPage {
  .applicationWizardPageInner {
    .wizard-content {
      margin: 0 1em;

      .applicationStep {
        height: 100%;
      }
    }

    .complianceFormRendererPage .form {
      background-color: var(--el-color-primary-light-9);

      .SectionComponent,
      .HeadingComponent {
        background-color: var(--el-color-primary-dark-3);
        margin: 0 -6px;
        color: #fff;
        font-size: 20px;
        .label {
          font-size: 1.25em;
        }
      }
    }

    .reviewAndSubmitStep {
      max-width: 1200px;
      margin: 1em auto 1em auto;
      display: flex;
      flex-direction: column;
      place-content: center;

      h2 {
        margin-top: 0;
        margin-bottom: 16px;
      }
    }
    @media (max-width: 500px) {
      h2 {
        font-size: 1.5rem;
      }
    }
  }
}
</style>
